import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadModelRegistry, type RegisteredModel } from '@/services/modelRegistryService';
import { PROVIDERS as STATIC_PROVIDERS, type ProviderConfig, type ProviderToolSupport } from '@/lib/llm/providerRegistry';

interface ModelRegistryContextType {
  providers: Record<string, ProviderConfig>;
  rawModels: RegisteredModel[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const ModelRegistryContext = createContext<ModelRegistryContextType | undefined>(undefined);

function getProviderDisplayName(provider: string): string {
  const displayNames: Record<string, string> = {
    'openai': 'OpenAI',
    'anthropic': 'Anthropic',
    'gemini': 'Google Gemini',
    'openai-realtime': 'OpenAI Realtime',
    'gemini-live': 'Google Gemini Live',
    'xai': 'xAI (Grok)',
    'openrouter': 'OpenRouter',
    'cerebras': 'Cerebras'
  };
  return displayNames[provider] || provider.charAt(0).toUpperCase() + provider.slice(1);
}

export const ModelRegistryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [providers, setProviders] = useState<Record<string, ProviderConfig>>(STATIC_PROVIDERS);
  const [rawModels, setRawModels] = useState<RegisteredModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchModels = async () => {
    try {
      setIsLoading(true);
      const models = await loadModelRegistry();
      setRawModels(models);

      // Reconstruct ProviderConfig mapping
      const mappedProviders: Record<string, ProviderConfig> = {};

      // Only filter active models for general consumption
      const activeModels = models.filter(m => m.is_active);

      activeModels.forEach(model => {
        const p = model.provider;
        if (!mappedProviders[p]) {
          mappedProviders[p] = {
            name: getProviderDisplayName(p),
            models: [],
            supportsReasoning: false,
            supportsImages: false,
            supportsVideo: false,
            supportsAudio: false,
            tools: {
              webSearch: false,
              imageGeneration: false,
              codeExecution: false,
              fileSearch: false,
              computerUse: false,
              codeInterpreter: false,
              xSearch: false,
              documentSearch: false,
              collectionsSearch: false,
              mcpTools: false,
            }
          };
        }

        // Avoid duplicate models in list
        if (!mappedProviders[p].models.includes(model.model_id)) {
          mappedProviders[p].models.push(model.model_id);
        }

        // Merge capabilities
        mappedProviders[p].supportsReasoning = mappedProviders[p].supportsReasoning || model.supports_reasoning;
        mappedProviders[p].supportsImages = mappedProviders[p].supportsImages || model.supports_images;
        mappedProviders[p].supportsVideo = mappedProviders[p].supportsVideo || model.supports_video;
        mappedProviders[p].supportsAudio = mappedProviders[p].supportsAudio || model.supports_audio;

        // Merge tool config
        if (model.tools) {
          Object.keys(mappedProviders[p].tools).forEach(toolKey => {
            const tk = toolKey as keyof ProviderToolSupport;
            if (model.tools[tk] !== undefined) {
              mappedProviders[p].tools[tk] = mappedProviders[p].tools[tk] || model.tools[tk];
            }
          });
        }
      });

      // If database returns empty (e.g. database reset/empty), fallback to static list
      if (Object.keys(mappedProviders).length === 0) {
        setProviders(STATIC_PROVIDERS);
      } else {
        setProviders(mappedProviders);
      }
      setError(null);
    } catch (err: any) {
      console.warn('[ModelRegistryContext] Failed to load model registry:', err?.message || err);
      setError(err);
      // Keep using static list on failure
      setProviders(STATIC_PROVIDERS);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  return (
    <ModelRegistryContext.Provider value={{ providers, rawModels, isLoading, error, refresh: fetchModels }}>
      {children}
    </ModelRegistryContext.Provider>
  );
};

export const useModelRegistry = () => {
  const context = useContext(ModelRegistryContext);
  if (!context) {
    throw new Error('useModelRegistry must be used within a ModelRegistryProvider');
  }
  return context;
};
